/**
 * The bell in the console header (P4-2, ADR-0078 §6).
 *
 * It shows an Ops person the alerts routed to a facet they hold, newest first, with how many they
 * have not read. It polls every minute: the platform pushes nothing to a browser until web push
 * (P4-5), and a minute is soon enough for alerts measured in hours while costing one small read.
 *
 * Each item names its type and severity in the reader's language, the Merchant's name in its own
 * script (isolated, so an Arabic name does not reorder an English line), when it was raised, and a
 * link to the screen where the matter is acted on. That screen asks for the justification a
 * cross-tenant read needs; the bell itself does not, because what it shows was already sent here.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { InboxItemView, InboxView } from '@tard/contracts';
import { getInbox, markInboxAllRead, markInboxRead } from '../api/inbox.js';
import { formatInstant } from '../i18n/money.js';

/** How often the bell asks again, in milliseconds. */
export const INBOX_POLL_MS = 60_000;

/** Where an item's matter is acted on. Each of these screens asks for its own justification. */
export function inboxTarget(item: InboxItemView): string {
  if (item.subject.kind === 'merchant') return `/ops/compliance?merchant=${encodeURIComponent(item.subject.reference)}`;
  switch (item.typeKey) {
    case 'ops.sla_breached':
      return '/ops/sla-breaches';
    case 'ops.custody_conflict':
    case 'ops.custody_frozen':
      return '/ops/exceptions';
    default:
      return '/ops';
  }
}

export function InboxBell(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<InboxView | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      setView((await getInbox()).inbox);
      setFailed(false);
    } catch {
      // The last answer stays on screen; the line below says it may be stale.
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), INBOX_POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  async function read(itemId: string): Promise<void> {
    try {
      await markInboxRead(itemId);
    } finally {
      await load();
    }
  }

  async function readAll(): Promise<void> {
    try {
      await markInboxAllRead();
    } finally {
      await load();
    }
  }

  const unread = view?.unread ?? 0;

  return (
    <div className="inbox-bell">
      <button
        type="button"
        className="btn"
        aria-expanded={open}
        aria-controls="inbox-panel"
        onClick={() => setOpen((value) => !value)}
      >
        {t('inbox.label')}{' '}
        <span className="num inbox-bell__count" data-testid="inbox-unread">
          {unread}
        </span>
      </button>
      {open && (
        <section id="inbox-panel" className="inbox" aria-label={t('inbox.label')}>
          <div className="inbox__head">
            <strong>{t('inbox.label')}</strong>
            <button type="button" className="btn" disabled={unread === 0} onClick={() => void readAll()}>
              {t('inbox.markAllRead')}
            </button>
          </div>
          {failed && (
            <p className="notice notice--warn" role="status">
              {t('inbox.loadFailed')}
            </p>
          )}
          {view !== null && view.items.length === 0 && <p className="muted">{t('inbox.empty')}</p>}
          {view !== null && view.items.length > 0 && (
            <ul className="inbox__list">
              {view.items.map((item) => (
                <li key={item.id} className={item.read ? 'inbox__item' : 'inbox__item inbox__item--unread'}>
                  <div>
                    <strong>{t(`inbox.type.${item.typeKey}`)}</strong>{' '}
                    <span className={`pill inbox__severity--${item.severity}`}>
                      {t(`inbox.severity.${item.severity}`)}
                    </span>
                  </div>
                  {typeof item.variables['merchantName'] === 'string' && (
                    <div>
                      <bdi>{item.variables['merchantName']}</bdi>
                    </div>
                  )}
                  <div className="inbox__meta">
                    <span className="localized">{formatInstant(item.createdAt, i18n.language)}</span>
                    <Link to={inboxTarget(item)} onClick={() => setOpen(false)}>
                      {t('inbox.open')}
                    </Link>
                    {!item.read && (
                      <button type="button" className="btn" onClick={() => void read(item.id)}>
                        {t('inbox.markRead')}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {/*
            The way to a person's own settings (P4-3c). In the panel's foot rather than the rail
            alone, because "stop showing me this" is a thought somebody has while reading the bell,
            and the rail is the other side of the screen. Closing on click, as every item's link
            does: the panel is a transient surface and leaving it open over a new route would make
            the next screen arrive half-covered.
          */}
          <p className="inbox__settings">
            <Link to="/me/notifications" onClick={() => setOpen(false)}>
              {t('me.notifications.title')}
            </Link>
          </p>
        </section>
      )}
    </div>
  );
}
